import React, { useMemo } from 'react';
import { Alert } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useDemoStatus } from '../../hooks/useConfigQueries';

const KNOWN_DEMO_SLUGS = new Set(['ciademo']);

function isKnownDemoSlug(slug) {
    if (!slug) return false;
    return KNOWN_DEMO_SLUGS.has(String(slug).trim().toLowerCase());
}

/**
 * Top banner when the session is on a demo sandbox tenant (English UI).
 * Uses GET /demo/status (master tenants.is_demo or shared sandbox slug e.g. ciademo).
 */
export default function DemoEnvironmentBanner() {
    const { token } = useAuth();
    const { data: demoStatusData } = useDemoStatus({ enabled: !!token });

    const demo = useMemo(() => {
        if (demoStatusData?.is_demo) {
            return demoStatusData;
        }
        const slug =
            typeof localStorage !== 'undefined'
                ? localStorage.getItem('tenant_id')
                : null;
        if (isKnownDemoSlug(slug)) {
            return { is_demo: true, phase: 'active', slug };
        }
        return null;
    }, [demoStatusData]);

    if (!demo?.is_demo) return null;

    const phase = demo.phase || 'active';
    const warn =
        phase === 'readonly' || phase === 'expired' ? 'warning' : 'info';

    return (
        <Alert
            variant={warn}
            className="rounded-0 border-0 mb-0 py-2 text-center small demo-environment-banner"
            style={{
                zIndex: 1056,
                backgroundColor: phase === 'active' ? 'var(--archon-demo-banner-active)' : undefined,
                color: 'var(--archon-white)',
                borderBottom: '2px solid var(--archon-accent)',
            }}
        >
            <strong>Demo Version</strong>
            <span className="ms-1">
                — You are using a sandbox environment for evaluation.
            </span>
            {phase === 'active' && ' Sample data may be reset.'}
            {phase === 'readonly' && ' Read-only: demo period ended.'}
            {phase === 'expired' && ' Sandbox expired.'}
            {demo.demo_expires_at && phase === 'active' && (
                <span className="ms-2 opacity-75">
                    Access ends: {new Date(demo.demo_expires_at).toLocaleString()}
                </span>
            )}
        </Alert>
    );
}
